import { AccessGate } from '../../shared/access/AccessControls';
import { useAllowedMutation, withAccess } from '../../shared/access/AccessControls';
import React, { useEffect, useMemo } from 'react';
import { Company } from '../../shared/types/company';
import Wrapper from '../../shared/components/Wrapper';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Button, Grid, Text, useToast } from '@chakra-ui/react';
import FormSelect from '../../shared/components/FormSelect';
import { pricingPlansApi } from '../../store/services/PricingPlansService';
import { useSelector } from 'react-redux';
import { selectPricingPlans } from '../../store/slices/pricingPlansSlice';
import { SORTING_CREATED_AT } from '../pricingPlans/constants';
import { ASC } from '../../shared/constants';
import { adminMutationError } from '../../shared/utils/adminMutationError';

const CompanyTariffPlan = ({ company }: { company: Company }) => {
  const billingAccount = useMemo(() => company?.billingAccount, [company]);

  const [trigger] = pricingPlansApi.useLazyGetPlansQuery();
  const plans = useSelector(selectPricingPlans);

  const toast = useToast();
  const [
    assignTariffPlan,
    { isLoading: isAssigning, isSuccess, isError, error, reset: resetAssignment },
  ] = useAllowedMutation(pricingPlansApi.useAssignBillingAccountMutation, 'assignBillingAccount');

  useEffect(() => {
    trigger({
      limit: 20,
      offset: 0,
      sorting: SORTING_CREATED_AT,
      order: ASC,
      query: '',
    });
  }, []);

  useEffect(() => {
    if (!isSuccess && !isError) return;
    toast({
      title: isSuccess ? 'Success.' : 'Error.',
      description: isSuccess
        ? 'The company tariff plan has been changed.'
        : adminMutationError(error, 'assignTariff'),
      status: isSuccess ? 'success' : 'error',
      position: 'top-right',
      duration: 5000,
      isClosable: true,
    });
    // The mutation invalidates Company; refresh the available plans as well.
    trigger({
      limit: 20,
      offset: 0,
      sorting: SORTING_CREATED_AT,
      order: ASC,
      query: '',
    });
    resetAssignment();
  }, [isSuccess, isError, error, toast, trigger, resetAssignment]);

  const defaultPlan = useMemo(
    () => plans.find((plan) => plan.isDefault),
    [plans],
  );

  useEffect(() => {
    reset({
      tariff_plan_id: billingAccount?.tariffPlanId || defaultPlan?.id || '',
    });
  }, [defaultPlan, billingAccount]);

  const plansOptions = useMemo(() => {
    return plans
      .filter((plan) => !plan.isDeleted && plan.isActive)
      .map((plan) => ({ value: plan.id, label: plan.name }));
  }, [plans]);

  const { register, handleSubmit, reset } = useForm<{
    tariff_plan_id: string;
  }>();

  const onSubmit: SubmitHandler<{ tariff_plan_id: string }> = (args) => {
    assignTariffPlan({
      billingAccountId: billingAccount?.id,
      planId: args.tariff_plan_id,
    });
  };

  return (
    <Wrapper>
      {(!company && <p>Loading billing info...</p>) || (
        <>
          <Text mb={'1rem'}>
            <b>Company tariff plan</b>
          </Text>
          {(!!billingAccount?.id && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid rowGap={'1rem'} gridTemplateColumns={'1fr'}>
                <FormSelect
                  register={register}
                  ph={'Tariff plan'}
                  name={'tariff_plan_id'}
                  labeledOptions={plansOptions}
                />
                <AccessGate action="ADMIN_TARIFF_PLAN_ASSIGN"><Button
                  colorScheme="orange"
                  type={'submit'}
                  isLoading={isAssigning}
                  w={'max-content'}
                >
                  Select plan
                </Button></AccessGate>
              </Grid>
            </form>
          )) || <p>Create billing account first</p>}
        </>
      )}
    </Wrapper>
  );
};

export default withAccess(CompanyTariffPlan, 'ADMIN_TARIFF_PLAN_ASSIGN');
